// src/components/sections/HeroSection.jsx
import React from "react";
import { useTheme } from "../../context/ThemeContext";
import Staffs from "../../assets/images/about/tech-guy.jpg";

const HeroSection = () => {
  const { isDark } = useTheme();
  
  const stats = [
    { value: "5+", label: "Industries Served" },
    { value: "100%", label: "Custom Solutions" },
    { value: "24/7", label: "Support Available" },
    { value: "Enterprise", label: "Grade Security" }
  ];

  return (
    <section className="relative min-h-screen overflow-hidden flex items-center pt-20">
      {/* Background Image - Always visible but with different overlays based on theme */}
      <div className="absolute inset-0 z-0">
        <img
          src={Staffs}
          alt="Programmer working on software solutions"
          className="w-full h-full object-cover object-center"
        />
        {/* Different overlays based on theme */}
        {isDark ? (
          <div className="absolute inset-0 bg-gradient-to-br from-black/30 via-black/90 to-black/10" />
        ) : (
          <div className="absolute inset-0 " />
        )}
      </div>

      {/* Gradient overlays for depth */}
      <div className={`absolute inset-0 z-0 ${isDark ? 'bg-gradient-to-r from-black/60 via-transparent to-black/40' : 'bg-gradient-to-r from-black/80  to-black/40'}`} />
      
      {/* Glow effects - only in dark mode */}
      {isDark && (
        <>
          <div className="absolute top-[-200px] left-[-100px] w-[500px] h-[500px] bg-blue-500/15 rounded-full blur-3xl z-0" />
          <div className="absolute bottom-[-250px] right-[-150px] w-[600px] h-[600px] bg-blue-600/15 rounded-full blur-3xl z-0" />
        </>
      )}

      {/* Grid pattern overlay */}
      <div
        className={`absolute inset-0 z-0 ${isDark ? 'opacity-[0.01]' : 'opacity-[0.02]'}`}
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), 
                            linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)`,
          backgroundSize: "70px 70px",
        }}
      />

      {/* Main content */}
      <div className="relative z-10 w-full mt-12">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-4xl">
            {/* Badge */}
            <div className="mb-6">
              <span className={`hidden md:flex inline-flex items-center gap-2 px-4 py-2 rounded-full border backdrop-blur-md text-xs tracking-[0.2em] uppercase font-medium ${
                isDark 
                  ? 'border-white/20 bg-black/40 text-blue-300'
                  : 'border-gray-300 bg-white/10 text-white'
              }`}>
                INNOVATIVE BUSINESS SOLUTIONS & SOFTWARE DEVELOPMENT
              </span>
            </div>

            {/* Heading */}
            <h1 className={`text-5xl sm:text-6xl lg:text-5xl font-bold leading-[1.1] tracking-tight ${isDark ? 'text-white' : 'text-gray-100'}`}>
              From complex challenges to
              <br />
              <span className={`${isDark? 'text-blue-800':''}`}>
                sustainable business solutions
              </span>
            </h1>

            {/* Description */}
            <p className={`mt-8 text-lg sm:text-xl leading-relaxed max-w-2xl ${isDark ? 'text-gray-200' : 'text-gray-100'}`}>
              We build SaaS products and custom software solutions for Construction,
              Manufacturing, Mining, Oil & Gas, and Food industries.<br/>
              Our solutions combine innovation and practical technology to help businesses
              work better, grow faster, and achieve lasting results.
            </p>

            {/* Buttons - gradient buttons remain the same for both themes */}
            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <button className="px-8 py-4 rounded-xl bg-gradient-to-r from-blue-700 to-blue-800 text-white font-semibold hover:scale-[1.02] transition-all duration-300 shadow-[0_0_40px_rgba(34,211,238,0.35)] hover:shadow-[0_0_60px_rgba(34,211,238,0.5)]">
                Get Started
              </button>

              <button className={`px-8 py-4 rounded-xl border backdrop-blur-md font-medium transition-all duration-300 ${
                isDark
                  ? 'border-white/20 bg-black/40 text-white hover:bg-white/10 hover:border-white/30'
                  : 'border-gray-300 bg-white/60 text-gray-800 hover:bg-white/80 hover:border-gray-400'
              }`}>
                Book a Demo
              </button>
            </div>

            {/* Stats */}
            <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-8 max-w-3xl">
              {stats.map((stat, index) => (
                <div key={index} className="backdrop-blur-sm">
                  <h3 className={`text-3xl sm:text-4xl font-bold ${isDark ? 'text-white' : 'text-white'}`}>{stat.value}</h3>
                  <p className={`mt-2 text-sm ${isDark ? 'text-gray-300' : 'text-white'}`}>{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className={`absolute bottom-0 left-0 right-0 h-32 z-0 ${isDark ? 'bg-gradient-to-t from-black to-transparent' : ''}`} />
    </section>
  );
};

export default HeroSection;